// Loads one module's entry (once, then cached), remembers it as the last module, and
// renders its routes inside the platform layout, behind an error boundary so one
// module's crash never takes the header or the dropdown with it (spec, Error handling).
import { Component, useEffect, useState } from 'react';
import { Routes, useLocation } from 'react-router';

import PlatformLayout from './PlatformLayout.jsx';
import { boundaryStateFor, entryStateFor, loadModuleEntry } from './module-entry.js';

const entries = new Map();

// How long a lazy module load may stay unsettled before it counts as failed (R14). A
// browser extension stalled a chunk forever in the owner's Chrome: with no deadline the
// page sits on "Loading …" with no Reload. 15 s is far past any real chunk download.
const MODULE_LOAD_TIMEOUT_MS = 15_000;

// The state is tagged with the module it was loaded for, and a state from another module
// is never returned (entryStateFor): the previous module's Provider, header actions and
// routes must not render under this manifest, not even for one frame.
function useModuleEntry(manifest) {
  const id = manifest.id;
  const [state, setState] = useState(() => ({ id, entry: entries.get(id) ?? null, error: null }));
  useEffect(() => {
    if (entries.has(id)) { setState({ id, entry: entries.get(id), error: null }); return undefined; }
    let cancelled = false;
    setState({ id, entry: null, error: null });
    // A success after the deadline still lands in `entries` (loadModuleEntry), so the
    // next open is instant, but this promise has already answered with the timeout and
    // the "Couldn't load" page stays put rather than flipping under the person.
    loadModuleEntry(manifest.load, id, entries, MODULE_LOAD_TIMEOUT_MS).then(
      (entry) => { if (!cancelled) setState({ id, entry, error: null }); },
      (error) => { if (!cancelled) setState({ id, entry: null, error }); },
    );
    return () => { cancelled = true; };
  }, [id, manifest]);
  return entryStateFor(state, id, entries);
}

function Problem({ title, children }) {
  return (
    <main className="mx-auto max-w-7xl px-4 py-16 text-center sm:px-6 lg:px-8">
      <h1 className="text-lg font-semibold">{title}</h1>
      <p className="mt-2 text-sm text-ink-secondary">{children}</p>
      <button type="button" className="btn-primary mt-4" onClick={() => window.location.reload()}>Reload</button>
    </main>
  );
}

// The module's code never arrived: a failed or stalled chunk.
function LoadFailed({ manifest, error }) {
  return (
    <Problem title={`Couldn’t load ${manifest.label}`}>
      {error?.name === 'TimeoutError'
        ? 'It is taking too long to arrive. A slow connection or a browser extension can cause this.'
        : 'This usually means Adora Ops was updated while this page was open.'}
    </Problem>
  );
}

// The code arrived, and one page threw while rendering. Different words on purpose: the
// load-failure text ("Adora Ops was updated") would send someone looking for a deploy.
function PageCrashed() {
  return (
    <Problem title="Something went wrong on this page">
      Reload to try again, or go to another page from the header.
    </Problem>
  );
}

class ModuleBoundary extends Component {
  constructor(props) { super(props); this.state = { failed: false, resetKey: props.resetKey }; }
  static getDerivedStateFromError() { return { failed: true }; }
  // Leaving the crashed page clears the crash (boundaryStateFor), so one broken page
  // never bricks the module.
  static getDerivedStateFromProps(props, state) { return boundaryStateFor(state, props.resetKey); }
  componentDidCatch(error) { console.error(`[shell] ${this.props.manifest.label} crashed`, error); }
  render() { return this.state.failed ? <PageCrashed /> : this.props.children; }
}

export default function ModuleHost({ manifest, available, remember }) {
  const { entry, error } = useModuleEntry(manifest);
  const { pathname } = useLocation();
  useEffect(() => { remember(manifest.id); }, [manifest.id, remember]);

  let body;
  if (error) body = <LoadFailed manifest={manifest} error={error} />;
  else if (!entry) body = <div className="p-8 text-sm text-ink-muted">Loading {manifest.label}…</div>;
  else {
    body = (
      <ModuleBoundary key={manifest.id} manifest={manifest} resetKey={pathname}>
        <Routes>{entry.routes}</Routes>
      </ModuleBoundary>
    );
  }

  return <PlatformLayout active={manifest} available={available} entry={entry}>{body}</PlatformLayout>;
}
